import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { InvoiceValidationPanel } from '../components/InvoiceValidationPanel';

interface VendorInvoice {
  id: number;
  vendor_name: string;
  invoice_number: string;
  invoice_date: string;
  total_amount: number;
  account_number?: string;
  notes?: string;
  line_items?: any[];
  raw_extracted_text?: string;
  pdf_url?: string;
  pdf_filename?: string;
  created_at: string;
  updated_at: string;
}

const VendorInvoiceDetailPage: React.FunctionComponent = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const [invoice, setInvoice] = useState<VendorInvoice | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [editing, setEditing] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    vendor_name: '',
    invoice_number: '',
    invoice_date: '',
    total_amount: '',
    account_number: '',
    notes: '',
  });

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/vendors/vendor_invoices/${id}`);
      const invoiceData = response.data;
      setInvoice(invoiceData);
      setFormData({
        vendor_name: invoiceData.vendor_name || '',
        invoice_number: invoiceData.invoice_number || '',
        invoice_date: invoiceData.invoice_date || '',
        total_amount: invoiceData.total_amount?.toString() || '',
        account_number: invoiceData.account_number || '',
        notes: invoiceData.notes || '',
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch invoice details';
      showErrorSnackBar(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`/vendors/vendor_invoices/${id}`, {
        vendor_invoice: formData,
      });
      showSuccessSnackBar('Invoice updated successfully');
      setEditing(false);
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to update invoice';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      await axios.delete(`/vendors/vendor_invoices/${id}`);
      showSuccessSnackBar('Invoice deleted successfully');
      history.push('/vendors/invoices');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to delete invoice';
      showErrorSnackBar(errorMessage);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box>
        <Typography variant="h6">Invoice not found</Typography>
      </Box>
    );
  }

  return (
    <Box style={{ width: '100%' }}>
      <Grid container spacing={2} style={{ marginBottom: 20 }}>
        <Grid item>
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/vendors/invoices')}>
            Back to List
          </Button>
        </Grid>
      </Grid>

      <Typography variant="h5" component="h5" style={{ marginBottom: 20 }}>
        Invoice Details
      </Typography>

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Vendor Name"
                value={editing ? formData.vendor_name : invoice.vendor_name}
                onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: !editing }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Invoice Number"
                value={editing ? formData.invoice_number : invoice.invoice_number}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: !editing }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Invoice Date"
                type={editing ? 'date' : 'text'}
                value={
                  editing
                    ? formData.invoice_date
                    : invoice.invoice_date
                      ? new Date(invoice.invoice_date).toLocaleDateString()
                      : 'N/A'
                }
                onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: !editing }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Total Amount"
                value={
                  editing
                    ? formData.total_amount
                    : invoice.total_amount
                      ? `$${parseFloat(invoice.total_amount.toString()).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : 'N/A'
                }
                onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: !editing }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Account Number"
                value={editing ? formData.account_number : invoice.account_number || 'N/A'}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: !editing }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={editing ? formData.notes : invoice.notes || 'No notes'}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                InputProps={{ readOnly: !editing }}
              />
            </Grid>

            {invoice.pdf_url && (
              <Grid item xs={12}>
                <Button variant="outlined" startIcon={<PictureAsPdfIcon />} href={invoice.pdf_url} target="_blank">
                  View PDF ({invoice.pdf_filename})
                </Button>
              </Grid>
            )}

            {invoice.line_items && invoice.line_items.length > 0 && (
              <Grid item xs={12}>
                <Divider style={{ margin: '16px 0' }} />
                <Typography variant="h6" gutterBottom>
                  Line Items ({invoice.line_items.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" aria-label="line items table">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Description</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Quantity</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Unit Price</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Amount</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.line_items.map((item: any, index: number) => (
                        <TableRow key={item.id || index} hover>
                          <TableCell>{item.description || 'N/A'}</TableCell>
                          <TableCell align="right">{item.quantity || '-'}</TableCell>
                          <TableCell align="right">
                            {item.unit_price ? `$${parseFloat(item.unit_price).toFixed(2)}` : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {item.amount ? `$${parseFloat(item.amount).toFixed(2)}` : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {invoice.raw_extracted_text && (
              <Grid item xs={12}>
                <Divider style={{ margin: '16px 0' }} />
                <Typography variant="h6" gutterBottom>
                  Raw Extracted Text
                </Typography>
                <TextField
                  value={invoice.raw_extracted_text}
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={10}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider style={{ margin: '16px 0' }} />
              <Grid container spacing={2}>
                {!editing ? (
                  <>
                    <Grid item>
                      <Button variant="contained" color="primary" onClick={() => setEditing(true)}>
                        Edit Invoice
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button variant="outlined" color="error" onClick={handleDelete}>
                        Delete Invoice
                      </Button>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item>
                      <Button variant="contained" color="primary" onClick={handleUpdate}>
                        Save Changes
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setEditing(false);
                          setFormData({
                            vendor_name: invoice.vendor_name || '',
                            invoice_number: invoice.invoice_number || '',
                            invoice_date: invoice.invoice_date || '',
                            total_amount: invoice.total_amount?.toString() || '',
                            account_number: invoice.account_number || '',
                            notes: invoice.notes || '',
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Validation Panel */}
      {invoice && <InvoiceValidationPanel invoiceId={invoice.id} onValidationComplete={fetchInvoice} />}
    </Box>
  );
};

export default VendorInvoiceDetailPage;
